import { type PayloadAction, createSlice, current } from '@reduxjs/toolkit';
import { addMinutes } from 'date-fns';

import {
    type DeliveryType,
    type Store,
    type TimeSlots,
    addRangeToLabels,
    getTimeSlotDate,
    isClosed,
    shuffleCurrentTimeSlotSelection
} from '@lib/entities/Store';
import { Either } from '@lib/monad';
import { type DeferredAction, withDeferred } from '@lib/redux/deferred';

/**
 * Redux twin of lib/state/Schedule.ts + `useSchedule.fetchTimeSlots` (JSAD-7433).
 *
 * `timeSlots` keeps the entity type (it carries `Date` objects); `lib/redux/store.ts` lists
 * `schedule.timeSlots` in `serializableCheck.ignoredPaths` and the slice is never persisted.
 * NOT reset on `auth/loggedOut`: the logout saga dispatches `resetSchedule()` itself, and a
 * session expiry keeps the selected store (lifecycle.ts).
 */
export interface ScheduleState {
    timeSlots: TimeSlots;
    currentDay: number;
    currentTime: number;
    store?: Store;
    closedIndefinitely: boolean;
    outOfSlots: boolean;
    /**
     * Monotonic token incremented each time a time-slot fetch STARTS. Used to
     * discard stale fetch results: concurrent `fetchTimeSlots` calls (e.g. the
     * delivery-type toggle fetching the OLD store while `visitStore` fetches the
     * NEW one) would otherwise race, and whichever network call resolved last
     * would win the `store` write — flipping checkout to the wrong store.
     */
    fetchSeq: number;
    /**
     * (Web) signals that the user wants to edit their location or
     * their delivery type for i.e. triggering the location modal.
     */
    hasPendingLocationChange: boolean;
}

export interface SetStoreAndTimeSlotsParams {
    store: Store;
    timeSlots: TimeSlots;

    /**
     * If passed, the returned labels will render as <time> - <time + range>
     * If 0 or undefined, labels will render as <time>
     * `range` in minutes
     */
    range?: number;

    /**
     * The `fetchSeq` token claimed (via `beginFetch`) when this fetch started.
     * If a newer fetch has since started, this (stale) result is dropped.
     */
    seq?: number;
}

/** `useSchedule.ts:19` fetchTimeSlots({ store, deliveryType, days = 2, cartItemCount }) */
export interface FetchTimeSlotsParams {
    store: Store;
    deliveryType: DeliveryType;
    days?: number;
    cartItemCount?: number;
}

/** `Schedule.ts:75` — `hasPendingLocationChange` is declared beside the actions (`:126`). */
const DefaultState: Omit<ScheduleState, 'store' | 'hasPendingLocationChange'> = {
    timeSlots: [],
    currentDay: 0,
    currentTime: 0,
    closedIndefinitely: false,
    outOfSlots: false,
    fetchSeq: 0
};

const initialState: ScheduleState = {
    ...DefaultState,
    hasPendingLocationChange: false
};

/** Saga-only request: the reducer is a no-op; the saga settles `meta.deferred`. */
const request = <P, T = void>() => ({
    reducer: (_state: unknown, _action: DeferredAction<P, T>) => {},
    prepare: withDeferred<P, T>()
});

const scheduleSlice = createSlice({
    name: 'schedule',
    initialState,
    reducers: {
        /**
         * Claim the next fetch token. Dispatch when a time-slot fetch STARTS and read
         * `fetchSeq` back in the same tick (`select` right after `put`, no suspension between
         * the two effects); pass it to `setStoreAndTimeSlots` when the fetch resolves so stale
         * results (superseded by a newer fetch) are dropped.
         */
        beginFetch: (state) => {
            state.fetchSeq += 1;
        },
        setStoreAndTimeSlots: (state, action: PayloadAction<SetStoreAndTimeSlotsParams>) => {
            const { store, timeSlots, range, seq } = action.payload;

            // Drop stale fetch results: if a newer fetch has started since this one
            // was claimed, ignore it so it can't overwrite `store` with an old value.
            if (seq !== undefined && seq !== state.fetchSeq) {
                return;
            }

            const { currentDay, currentTime, timeSlots: lastSlots } = current(state);

            state.timeSlots = range ? addRangeToLabels(timeSlots, range) : timeSlots;
            state.closedIndefinitely = timeSlots.length === 0;
            state.store = store;

            const result = shuffleCurrentTimeSlotSelection({
                timeSlots,
                lastSlots,
                currentDay,
                currentTime
            });

            if (Either.isLeft(result)) {
                state.outOfSlots = true;
                return;
            }

            const next = Either.unwrap(result);
            state.currentDay = next.currentDay;
            state.currentTime = next.currentTime;
        },
        clearOutOfSlots: (state) => {
            state.outOfSlots = false;
        },
        setHasPendingLocationChange: (state, action: PayloadAction<boolean>) => {
            state.hasPendingLocationChange = action.payload;
        },
        changeDay: (state, action: PayloadAction<number>) => {
            const currentDay = action.payload;
            state.currentDay = state.timeSlots[currentDay] ? currentDay : state.currentDay;
        },
        changeTime: (state, action: PayloadAction<number>) => {
            const currentTime = action.payload;
            state.currentTime = state.timeSlots[state.currentDay]?.[1]?.[currentTime]
                ? currentTime
                : state.currentTime;
        },
        /**
         * Bumps `fetchSeq` (never resets it) so this is monotonic: setting a store here
         * invalidates any in-flight fetchTimeSlots claimed before the reset, and DefaultState's
         * `fetchSeq: 0` doesn't roll the counter back. `hasPendingLocationChange` is preserved
         * (it is not part of the Zustand `DefaultState`).
         */
        resetSchedule: (state, action: PayloadAction<Store | undefined>) => {
            Object.assign(state, DefaultState, {
                store: action.payload,
                fetchSeq: state.fetchSeq + 1
            });
        },

        // ── Saga-only request (useSchedule.fetchTimeSlots) — resolves the raw `Date[]` ─────
        fetchTimeSlotsRequest: request<FetchTimeSlotsParams, Date[]>()
    }
});

export const {
    beginFetch,
    setStoreAndTimeSlots,
    clearOutOfSlots,
    setHasPendingLocationChange,
    changeDay,
    changeTime,
    resetSchedule,
    fetchTimeSlotsRequest
} = scheduleSlice.actions;

export const selectSchedule = (state: { schedule: ScheduleState }) => state.schedule;
export const selectScheduleStore = (state: { schedule: ScheduleState }) => state.schedule.store;
export const selectFetchSeq = (state: { schedule: ScheduleState }) => state.schedule.fetchSeq;

/**
   Has this order been scheduled for the future (any time slot other than 0, 0)
 */
export const selectIsScheduled = (state: { schedule: ScheduleState }): boolean => {
    const { store, currentDay, currentTime } = state.schedule;

    if (!store) {
        return false;
    }

    if (isClosed(store)) {
        return true;
    }

    return currentDay !== 0 || currentTime !== 0;
};

/**
   In practice, `selectPickupTime()` should not ever return
   undefined since the `currentDay` and `currentTime` default to
   `0, 0` and will do the right thing as long as the pickups
   have loaded from the api.

   `selectPickupTimeSafe` falls back if times haven't loaded, assuming
   20m in the future.
*/
export const selectPickupTimeSafe = (state: { schedule: ScheduleState }): Date =>
    selectPickupTime(state) ?? addMinutes(new Date(), 20);

export const selectPickupTime = (state: { schedule: ScheduleState }): Date | undefined => {
    const { timeSlots: pickups, currentDay, currentTime } = state.schedule;
    return getTimeSlotDate(pickups, currentDay, currentTime);
};

export const selectStoreId = (state: { schedule: ScheduleState }): number | undefined => {
    return state.schedule.store?.id;
};

export const selectCoordinates = (state: {
    schedule: ScheduleState;
}): { lat?: number; lng?: number } => {
    const store = state.schedule.store;

    return {
        lat: store?.lat,
        lng: store?.lng
    };
};

export default scheduleSlice.reducer;
